import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  Chip,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  Send as SendIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  Help as HelpIcon,
} from '@material-ui/icons';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { VolunteerCommonProfile } from '../../__generated__/VolunteerCommonProfile';
import { useUser } from '../../../../../global/auth/useUser';
import { SelectVolunteersDialog } from '../../../../../common/select-volunteers/SelectVolunteersDialog';
import { GetSelectVolunteerFragment } from '../../../../../common/select-volunteers/__generated__/GetSelectVolunteerFragment';
import { getAvatarColour } from '../../../../general/user-profile/components/Avatar';
import { useRegionCodes } from '../../../../../common/auto-location-field/use-region-codes/use-region-codes';
import { useSmsCost } from '../../../../../common/sms/use-sms-cost';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';
import { useAdvancedCommunications } from '../../../communications/use-advanced-communications';

type VolunteerSmsEntry =
  | VolunteerCommonProfile
  | {
      profile: {
        profileId: string;
        preferredName: string;
        lastName: string;
        phone?: string;
        avatarUrl?: string | null;
      };
      volunteerId?: string;
    };

interface VolunteerCommonProfileSmsDialogProps {
  volunteers: VolunteerSmsEntry | VolunteerSmsEntry[];
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
}

interface SendSmsRequest {
  profileIds: string[];
  sentBy: string;
  message: string;
  senderName?: string | null;
  allowReplies?: boolean;
}

interface SmsRecipient {
  profileId: string;
  volunteerId?: string;
  name: string;
  avatarUrl?: string | null;
}

const useStyles = makeStyles(() =>
  createStyles({
    sendButton: {
      textTransform: 'none',
      backgroundColor: '#6366f1',
      color: 'white',
      '&:hover': {
        backgroundColor: '#5856eb',
      },
    },
    cancelButton: {
      textTransform: 'none',
    },
    chip: {
      backgroundColor: '#f5f5f5',
      marginRight: '8px',
      marginBottom: '4px',
      '& .MuiChip-avatar': {
        width: '24px',
        height: '24px',
      },
    },
    costDisplay: {
      backgroundColor: '#f5f5f5',
      color: '#666',
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 500,
      marginRight: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    helpIcon: {
      marginRight: '8px',
      fontSize: '18px',
      color: '#666',
      cursor: 'pointer',
      '&:hover': {
        color: '#555',
      },
    },
  })
);

export const VolunteerCommonProfileSmsDialog = (props: VolunteerCommonProfileSmsDialogProps) => {
  const { open, volunteers, onClose, onSent } = props;
  const { isMobile } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();
  const classes = useStyles();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recipients, setRecipients] = useState<SmsRecipient[]>([]);
  const [selectVolunteersOpen, setSelectVolunteersOpen] = useState(false);
  const [manageRecipientsOpen, setManageRecipientsOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [senderName, setSenderName] = useState<string>('org');
  const [allowReplies, setAllowReplies] = useState(false);

  const sendSms = useEndpointFetch<SendSmsRequest>('/vm/comms/send-sms');
  const { lightTextColor } = getAlertCardColors('info');
  const { primaryRegion } = useRegionCodes();
  const org = useOrgInfo();
  const advancedCommunications = useAdvancedCommunications();
  const { costPerUnit: SMS_COST_PER_UNIT, messageUnits: getUnits } = useSmsCost();

  const MAX_DISPLAY_RECIPIENTS = 8;

  const volunteersArray = Array.isArray(volunteers) ? volunteers : [volunteers];

  React.useEffect(() => {
    if (recipients.length > 0 || volunteersArray.length === 0) return;
    setRecipients(
      volunteersArray.map((v) => ({
        profileId: v.profile.profileId,
        volunteerId: (v as any).volunteerId,
        name: `${v.profile.preferredName} ${v.profile.lastName}`,
        avatarUrl: v.profile.avatarUrl,
      }))
    );
  }, [volunteers, recipients.length]);

  // Get sender prefix for calculation
  const getSenderPrefix = () => {
    if (senderName === 'org') {
      return `${org?.name} says: `;
    }
    if (senderName === 'user') {
      return `${user.firstName} ${user.lastName} at ${org?.name} says: `;
    }
    return '';
  };

  const senderPrefix = getSenderPrefix();
  const fullMessage = senderPrefix + message;

  // Calculate message units and cost including the sender prefix
  const messageUnits = getUnits(fullMessage);
  const totalUnits = messageUnits * recipients.length;
  const totalCost = totalUnits * SMS_COST_PER_UNIT;

  const handleAddVolunteers = (selectedVolunteers: GetSelectVolunteerFragment[]) => {
    const newRecipients = selectedVolunteers
      .filter((vol) => !recipients.some((r) => r.profileId === vol.profile.profileId))
      .map((vol) => ({
        profileId: vol.profile.profileId,
        volunteerId: vol.volunteerId,
        name: `${vol.profile.preferredName} ${vol.profile.lastName}`,
        avatarUrl: vol.profile.avatarUrl,
      }));

    setRecipients((prev) => [...prev, ...newRecipients]);
    setSelectVolunteersOpen(false);
  };

  const handleRemoveRecipient = (profileId: string) => {
    setRecipients((prev) => prev.filter((r) => r.profileId !== profileId));
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      setSnackbar({
        message: 'Please enter a message',
        variant: 'error',
        open: true,
      });
      return;
    }

    if (recipients.length === 0) {
      setSnackbar({
        message: 'No recipients selected',
        variant: 'error',
        open: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await sendSms.run({
        profileIds: recipients.map((r) => r.profileId),
        sentBy: user.profileId,
        message: message.trim(),
        senderName: senderName === 'user' ? `${user.firstName} ${user.lastName}` : null,
        allowReplies: advancedCommunications && allowReplies,
      });

      if (response && response.ok) {
        setSnackbar({
          message: `SMS sent successfully to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`,
          variant: 'success',
          open: true,
        });
        if (onSent) onSent();
        handleClose();
      } else {
        setSnackbar({
          message: 'Failed to send SMS',
          variant: 'error',
          open: true,
        });
      }
    } catch (error) {
      setSnackbar({
        message: 'An error occurred while sending the SMS',
        variant: 'error',
        open: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setRecipients([]);
    setSenderName('org');
    setAllowReplies(false);
    onClose();
  };

  const volunteerName = recipients.length === 1 ? recipients[0].name : `${recipients.length} recipients`;

  return (
    <>
      <FullscreenDialog
        open={open}
        close={handleClose}
        title={`SMS ${volunteerName}`}
        fullScreen={isMobile}
        maxWidth='sm'
        fullWidth
      >
        <Grid container spacing={2} style={{ paddingBottom: 10 }}>
          <Grid item xs={12}>
            <Box display='flex' alignItems='center' mb={2}>
              <Typography variant='body2' color='textSecondary' style={{ marginRight: '8px' }}>
                To:
              </Typography>
              <Box display='flex' flexWrap='wrap' alignItems='center' style={{ gap: '1' }}>
                {recipients.slice(0, MAX_DISPLAY_RECIPIENTS).map((recipient) => (
                  <Chip
                    key={recipient.profileId}
                    avatar={
                      recipient.avatarUrl ? (
                        <Avatar src={recipient.avatarUrl} alt={recipient.name} style={{ width: 24, height: 24 }} />
                      ) : (
                        <Avatar
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: `#${getAvatarColour(
                              recipient.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                            )}`,
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 700,
                          }}
                        >
                          {recipient.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                        </Avatar>
                      )
                    }
                    label={recipient.name}
                    onDelete={recipients.length > 1 ? () => handleRemoveRecipient(recipient.profileId) : undefined}
                    deleteIcon={<CloseIcon />}
                    variant='outlined'
                    className={classes.chip}
                  />
                ))}
                {recipients.length > MAX_DISPLAY_RECIPIENTS && (
                  <Chip
                    avatar={<PeopleIcon style={{ fontSize: 16 }} />}
                    label={`+${recipients.length - MAX_DISPLAY_RECIPIENTS} more`}
                    onClick={() => setManageRecipientsOpen(true)}
                    variant='outlined'
                    style={{
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      cursor: 'pointer',
                      marginRight: '8px',
                      marginBottom: '4px',
                    }}
                  />
                )}
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() => setSelectVolunteersOpen(true)}
                  style={{
                    textTransform: 'none',
                    color: '#1976d2',
                    fontSize: '14px',
                  }}
                >
                  Add more
                </Button>
              </Box>
            </Box>
          </Grid>

          {recipients.length > 10 && (
            <Grid item xs={12}>
              <AlertCard variant='info'>
                <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 700, color: lightTextColor }}>
                  We recommend using bulletins if you&apos;re communicating to lots of volunteers.
                </Typography>
              </AlertCard>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControl variant='outlined' fullWidth>
              <InputLabel>From</InputLabel>
              <Select value={senderName} onChange={(e) => setSenderName(e.target.value as string)} label='From'>
                <MenuItem value='org'>{org?.name}</MenuItem>
                <MenuItem value='user'>{`${user.firstName} ${user.lastName}`}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {advancedCommunications && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowReplies}
                    onChange={(e) => setAllowReplies(e.target.checked)}
                    color='primary'
                    size='small'
                  />
                }
                label={
                  <Box>
                    <Typography variant='body2' style={{ color: '#444444' }}>
                      Allow replies
                    </Typography>
                    <Typography variant='caption' style={{ color: '#666' }}>
                      {allowReplies
                        ? 'Sent from a number volunteers can reply to. Replies appear in the Inbox.'
                        : `Sent from the ${org?.name ?? 'Volaby'} sender name, which cannot receive replies.`}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Box mt={2}>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder='Type your SMS message here...'
                variant='outlined'
                required
                inputProps={{
                  maxLength: 1600, // Reasonable SMS length limit
                }}
                helperText={`${message.length} characters - ${messageUnits} message unit${
                  messageUnits !== 1 ? 's' : ''
                }`}
              />
            </Box>
          </Grid>

          <Grid
            container
            item
            justify='space-between'
            alignItems='center'
            style={{ padding: '8px 0', marginTop: '16px' }}
          >
            <Box className={classes.costDisplay}>
              <HelpIcon className={classes.helpIcon} onClick={() => setHelpDialogOpen(true)} />
              {totalUnits} MU - AU${totalCost.toFixed(2)}
            </Box>
            <Box>
              <Button
                onClick={handleClose}
                variant='text'
                disabled={isSubmitting}
                className={classes.cancelButton}
                style={{ marginRight: '8px' }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                disableElevation
                onClick={handleSubmit}
                disabled={isSubmitting || sendSms.isLoading || recipients.length === 0 || !message.trim()}
                startIcon={<SendIcon />}
                className={classes.sendButton}
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </FullscreenDialog>

      {selectVolunteersOpen && (
        <SelectVolunteersDialog
          open={selectVolunteersOpen}
          activity={{
            activityId: '', // Not needed for this use case
            name: '',
            program: { programId: '', name: '' },
          }}
          ignoredVolunteerIds={recipients.map((r) => r.volunteerId).filter(Boolean)}
          handleClose={() => setSelectVolunteersOpen(false)}
          handleAdd={handleAddVolunteers}
          enableGroupByActivity={false}
          dialogContentText='Select additional volunteers to send this SMS to:'
        />
      )}

      {manageRecipientsOpen && (
        <Dialog open={manageRecipientsOpen} onClose={() => setManageRecipientsOpen(false)} maxWidth='sm' fullWidth>
          <DialogTitle>Manage Recipients ({recipients.length})</DialogTitle>
          <DialogContent>
            <List>
              {recipients.map((recipient) => (
                <ListItem key={recipient.profileId}>
                  <ListItemAvatar>
                    {recipient.avatarUrl ? (
                      <Avatar src={recipient.avatarUrl} alt={recipient.name} />
                    ) : (
                      <Avatar
                        style={{
                          backgroundColor: `#${getAvatarColour(
                            recipient.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                          )}`,
                          color: 'white',
                        }}
                      >
                        {recipient.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText primary={recipient.name} />
                  <ListItemSecondaryAction>
                    <Checkbox
                      edge='end'
                      checked
                      onChange={() => handleRemoveRecipient(recipient.profileId)}
                      disabled={recipients.length === 1}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManageRecipientsOpen(false)} variant='text' style={{ textTransform: 'none' }}>
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {helpDialogOpen && (
        <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth='sm' fullWidth>
          <DialogTitle>
            <Box display='flex' alignItems='center'>
              <HelpIcon style={{ marginRight: '8px', color: '#666', fontSize: '24px' }} />
              SMS Message Units (MU)
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant='body1' paragraph>
              Message Units (MU) represent the number of SMS segments required to send your message.
            </Typography>
            <Typography variant='body2' paragraph>
              <strong>How it works:</strong>
            </Typography>
            <ul style={{ paddingLeft: '20px' }}>
              <li>
                <Typography variant='body2'>
                  Standard SMS messages can contain up to 160 characters, or 70 if the message includes emoji or other
                  special characters
                </Typography>
              </li>
              <li>
                <Typography variant='body2'>
                  Longer messages are split into segments of 153 characters (67 with special characters)
                </Typography>
              </li>
              <li>
                <Typography variant='body2'>Each segment counts as 1 Message Unit</Typography>
              </li>
              <li>
                <Typography variant='body2'>
                  Cost is calculated as: (Message Units) × (Recipients) × AU${SMS_COST_PER_UNIT.toFixed(2)}
                </Typography>
              </li>
              <li>
                <Typography variant='body2'>
                  A sender prefix is automatically added to each message (e.g., &quot;Organisation says:&quot; or
                  &quot;John Smith at Organisation says:&quot;)
                </Typography>
              </li>
            </ul>
            <Box mt={2}>
              <Typography variant='body2' color='textSecondary'>
                <strong>Example:</strong> A 200-character message to 5 recipients = 2 MU × 5 = 10 total units = AU$
                {(10 * SMS_COST_PER_UNIT).toFixed(2)}
              </Typography>
            </Box>
            <Box mt={1}>
              <Typography variant='body2' color='textSecondary'>
                <strong>Current rate:</strong> AU${SMS_COST_PER_UNIT.toFixed(2)} per message unit (
                {primaryRegion === 'au' ? 'Australian' : 'International'} rate)
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setHelpDialogOpen(false)}
              variant='text'
              color='primary'
              style={{ textTransform: 'none' }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
